import { type ShallowRef, nextTick } from 'vue'

import { usePreferredReducedMotion } from '@vueuse/core'

import { getActiveViewTransition } from '~/plugins/pdpPickerViewTransition.client'

import type { ChipTag } from '../ConditionGuidance.types'

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

const EASING_EXPRESSIVE_EXIT = 'cubic-bezier(0.6, 0, 1, 1)'
const EASING_EXPRESSIVE_DEFAULT = 'cubic-bezier(0.5, 0, 0.2, 1)'
const FLIP_DURATION_MS = 300
const COLOR_BRAND_MID = '#EEDCFC'

/** Per-pill stagger: each subsequent pill adds 150 ms to all delays. */
const PILL_STAGGER_MS = 150

function cleanupElement(el: HTMLElement) {
  Object.assign(el.style, { transform: '', opacity: '' })
}

// ---------------------------------------------------------------------------
// Premium scale-bounce entrance animation
// ---------------------------------------------------------------------------

const premiumScaleBounce = {
  onBeforeEnter(el: HTMLElement) {
    Object.assign(el.style, { opacity: '0', transform: 'scale(0)' })
  },

  onEnter(el: HTMLElement) {
    const baseBg = getComputedStyle(el).backgroundColor

    el.animate([{ transform: 'scale(0)' }, { transform: 'scale(1)' }], {
      duration: 600,
      delay: 600,
      easing: 'cubic-bezier(0.24, 1.56, 0.63, 1)',
      fill: 'forwards',
    })

    el.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 300,
      delay: 600,
      easing: 'linear',
      fill: 'forwards',
    })

    el.animate(
      [{ backgroundColor: COLOR_BRAND_MID }, { backgroundColor: baseBg }],
      { duration: 1000, delay: 600, easing: 'linear', fill: 'forwards' },
    )
  },
}

// ---------------------------------------------------------------------------
// Swap-settled coordination
// ---------------------------------------------------------------------------

/**
 * Module-level tracking so the premium chip entrance can wait until all
 * non-premium chip swap animations (exit + FLIP) have finished.
 *
 * - `trackSwapStart()` increments the pending count and creates a promise
 *   that resolves when all pending swaps settle.
 * - `trackSwapSettled()` decrements the count and resolves when it hits 0.
 * - `whenSwapsSettled()` returns the current promise (already resolved if
 *   no swaps are in progress).
 */
let pendingSwaps = 0
let swapsSettledPromise: Promise<void> = Promise.resolve()
let resolveSwapsSettled: (() => void) | undefined

function trackSwapStart() {
  if (pendingSwaps === 0) {
    swapsSettledPromise = new Promise((resolve) => {
      resolveSwapsSettled = resolve
    })
  }
  pendingSwaps += 1
}

function trackSwapSettled() {
  pendingSwaps = Math.max(0, pendingSwaps - 1)
  if (pendingSwaps === 0) {
    resolveSwapsSettled?.()
    resolveSwapsSettled = undefined
  }
}

function whenSwapsSettled(): Promise<void> {
  return swapsSettledPromise
}

// ---------------------------------------------------------------------------
// useChipTransitionHooks – TransitionGroup JS hooks (add / remove chips)
// ---------------------------------------------------------------------------

/**
 * Provides TransitionGroup JS hooks for animated guidance chips.
 *
 * - Non-premium chips: opacity fade (300 ms in, 150 ms out)
 * - Premium chips (detected via `data-chip-premium` attribute):
 *     scaleBounce entrance + purple background pulse
 *
 * All animations use the Web Animation API so they stay off the main thread
 * and clean up automatically via `animation.finished`.
 */
export function useChipTransitionHooks() {
  const prefersReducedMotion = usePreferredReducedMotion()

  function onBeforeEnter(el: Element) {
    const htmlEl = el as HTMLElement

    if (prefersReducedMotion.value === 'reduce') {
      return
    }

    // During view transitions, show elements immediately for the crossfade
    // snapshot. The entrance animation runs after the crossfade in onEnter.
    if (getActiveViewTransition()) {
      return
    }

    if (htmlEl.hasAttribute('data-chip-premium')) {
      premiumScaleBounce.onBeforeEnter(htmlEl)
    } else {
      htmlEl.style.opacity = '0'
    }
  }

  function onEnter(el: Element, done: () => void) {
    const htmlEl = el as HTMLElement

    if (prefersReducedMotion.value === 'reduce') {
      htmlEl.style.opacity = ''
      done()

      return
    }

    const isPremium = htmlEl.hasAttribute('data-chip-premium')
    const viewTransition = getActiveViewTransition()

    // During a view transition, defer entrance animations until the
    // crossfade completes so they play on the visible page.
    if (viewTransition) {
      viewTransition.then(() => {
        if (isPremium) {
          premiumScaleBounce.onBeforeEnter(htmlEl)
          requestAnimationFrame(() => {
            premiumScaleBounce.onEnter(htmlEl)
          })
        } else {
          htmlEl.style.opacity = '0'
          requestAnimationFrame(() => {
            const fade = htmlEl.animate([{ opacity: 0 }, { opacity: 1 }], {
              duration: 300,
              easing: 'ease-in-out',
              fill: 'forwards',
            })

            fade.finished
              .then(() => {
                fade.cancel()
                htmlEl.style.opacity = ''
                done()
              })
              .catch(() => done())
          })
        }
      })

      return
    }

    requestAnimationFrame(() => {
      if (isPremium) {
        // Wait for all non-premium chip swaps (exit + FLIP) to settle
        // before starting the 600 ms delayed scaleBounce entrance.
        whenSwapsSettled().then(() => {
          premiumScaleBounce.onEnter(htmlEl)
        })

        return
      }

      const fade = htmlEl.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 300,
        easing: 'ease-in-out',
        fill: 'forwards',
      })

      fade.finished
        .then(() => {
          fade.cancel()
          htmlEl.style.opacity = ''
          done()
        })
        .catch(() => done())
    })
  }

  function onLeave(el: Element, done: () => void) {
    const htmlEl = el as HTMLElement

    if (prefersReducedMotion.value === 'reduce') {
      done()

      return
    }

    const container = htmlEl.parentElement
    if (container) {
      const elRect = htmlEl.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      Object.assign(htmlEl.style, {
        position: 'absolute',
        left: `${elRect.left - containerRect.left}px`,
        top: `${elRect.top - containerRect.top}px`,
        width: `${elRect.width}px`,
        height: `${elRect.height}px`,
        margin: '0',
      })
    }

    requestAnimationFrame(() => {
      const fadeOut = htmlEl.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 150,
        easing: 'ease-in-out',
        fill: 'forwards',
      })

      fadeOut.finished
        .then(() => {
          fadeOut.cancel()
          done()
        })
        .catch(() => done())
    })
  }

  return {
    onBeforeEnter,
    onEnter,
    onLeave,
  }
}

// ---------------------------------------------------------------------------
// Sibling FLIP – animate siblings when a chip's width changes
// ---------------------------------------------------------------------------

/** Track in-flight sibling FLIP animations to cancel on rapid changes. */
const siblingFlipAnimations = new WeakMap<HTMLElement, Animation>()

/**
 * Snapshot sibling positions, snap chip to new width, and animate
 * siblings from old positions to new positions via transform.
 *
 * Must be called inside a nextTick (after Vue has flushed the DOM)
 * while the chip still has its explicit old width.
 *
 * @returns `true` if any sibling animations were started.
 */
function flipSiblingsOnWidthChange(chipElement: HTMLElement): boolean {
  const container = chipElement.parentElement
  if (!container) return false

  const siblings = Array.from(container.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement && c !== chipElement,
  )

  // FIRST – capture positions while chip is still at old width
  const firstRects = new Map<HTMLElement, { left: number; top: number }>()
  const containerRect = container.getBoundingClientRect()
  const containerHeight = container.offsetHeight

  for (const sib of siblings) {
    const r = sib.getBoundingClientRect()
    firstRects.set(sib, {
      left: r.left - containerRect.left,
      top: r.top - containerRect.top,
    })
  }

  // Snap chip to natural new width (content is at opacity 0)
  Object.assign(chipElement.style, { width: '' })

  // LAST – measure new positions (getBoundingClientRect forces sync layout)
  const newContainerRect = container.getBoundingClientRect()

  // Lock container height to prevent collapse during transform animations
  container.style.minHeight = `${containerHeight}px`

  const pending: Animation[] = []

  for (const [sib, first] of firstRects) {
    if (!sib.isConnected) continue

    // Cancel any in-flight FLIP animation on this sibling
    siblingFlipAnimations.get(sib)?.cancel()

    const lastRect = sib.getBoundingClientRect()
    const dx = first.left - (lastRect.left - newContainerRect.left)
    const dy = first.top - (lastRect.top - newContainerRect.top)

    if (dx === 0 && dy === 0) continue

    // INVERT + PLAY – animate from old position to new via transform
    const anim = sib.animate(
      [
        { transform: `translate(${dx}px, ${dy}px)` },
        { transform: 'translate(0, 0)' },
      ],
      {
        duration: FLIP_DURATION_MS,
        easing: EASING_EXPRESSIVE_DEFAULT,
        composite: 'accumulate',
      },
    )

    siblingFlipAnimations.set(sib, anim)
    pending.push(anim)

    anim.finished
      .then(() => siblingFlipAnimations.delete(sib))
      .catch(() => siblingFlipAnimations.delete(sib))
  }

  // Release container height lock after all animations finish
  Promise.all(
    pending.map((a) => a.finished.catch(Function.prototype as () => void)),
  )
    .then(() => {
      container.style.minHeight = ''
    })
    .catch(() => {
      container.style.minHeight = ''
    })

  return pending.length > 0
}

// ---------------------------------------------------------------------------
// useChipSwapAnimation – Within-chip content swap (non-premium grade change)
// ---------------------------------------------------------------------------

/**
 * Run exit animation on an element (move up + fade out).
 * Resolves when the fade completes; rejects if cancelled.
 */
function exitElement(
  el: HTMLElement,
  stagger: number,
  exit: { moveDelay: number; opacityDelay: number; opacityDuration: number },
  activeAnimations: Animation[],
): Promise<void> {
  const exitMove = el.animate(
    [{ transform: 'translateY(0)' }, { transform: 'translateY(-20px)' }],
    {
      duration: 300,
      delay: stagger + exit.moveDelay,
      easing: EASING_EXPRESSIVE_EXIT,
      fill: 'forwards',
    },
  )

  const exitFade = el.animate([{ opacity: '1' }, { opacity: '0' }], {
    duration: exit.opacityDuration,
    delay: stagger + exit.opacityDelay,
    easing: 'linear',
    fill: 'forwards',
  })

  activeAnimations.push(exitMove, exitFade)

  return exitFade.finished.then(() => {
    exitMove.cancel()
    exitFade.cancel()
    Object.assign(el.style, { transform: 'translateY(20px)', opacity: '0' })
  })
}

/**
 * Run enter animation on an element (move up from below + fade in).
 */
function enterElement(
  el: HTMLElement,
  enter: { moveDuration: number; opacityDuration: number },
  activeAnimations: Animation[],
) {
  const enterMove = el.animate(
    [{ transform: 'translateY(20px)' }, { transform: 'translateY(0)' }],
    {
      duration: enter.moveDuration,
      easing: EASING_EXPRESSIVE_DEFAULT,
      fill: 'forwards',
    },
  )

  const enterFade = el.animate([{ opacity: '0' }, { opacity: '1' }], {
    duration: enter.opacityDuration,
    easing: 'linear',
    fill: 'forwards',
  })

  activeAnimations.push(enterMove, enterFade)

  enterFade.finished
    .then(() => {
      enterMove.cancel()
      enterFade.cancel()
      cleanupElement(el)
    })
    .catch(() => cleanupElement(el))
}

/**
 * Composable that manages the three-phase animation for non-premium
 * guidance chips when the grade picker changes:
 *
 *   1. **Exit** – icon & label fade out and move up (staggered)
 *   2. **FLIP** – swap content, snap chip to new width, animate siblings
 *   3. **Enter** – icon & label fade in from below (after siblings settle)
 *
 * Returns `animateSwap` which should be called from a `watch` on the tag prop.
 * When the icon is identical between old and new tag, no animation runs.
 */
type TemplateRef = Readonly<ShallowRef<HTMLElement | null>>

export function useChipSwapAnimation(
  chipEl: TemplateRef,
  iconEl: TemplateRef,
  labelEl: TemplateRef,
) {
  let activeAnimations: Animation[] = []

  function cancelActiveAnimations() {
    for (const anim of activeAnimations) {
      anim.cancel()
    }
    activeAnimations = []
  }

  const prefersReducedMotion = usePreferredReducedMotion()

  /**
   * Animate the chip content swap. Returns `true` if animation was started,
   * `false` if content was updated immediately (identical icon, reduced motion,
   * or missing elements).
   */
  function animateSwap(
    newTag: ChipTag,
    oldTag: ChipTag | undefined,
    index: number,
    updateIcon: (icon: string) => void,
    updateText: (text: string) => void,
  ): boolean {
    if (!oldTag || newTag.icon === oldTag.icon) {
      updateIcon(newTag.icon)
      updateText(newTag.text)

      return false
    }

    if (prefersReducedMotion.value === 'reduce') {
      updateIcon(newTag.icon)
      updateText(newTag.text)

      return false
    }

    // During a page-level view transition (cross-slug navigation),
    // update content immediately for the crossfade snapshot, then
    // run entrance animations after the crossfade completes.
    const viewTransition = getActiveViewTransition()
    if (viewTransition) {
      updateIcon(newTag.icon)
      updateText(newTag.text)

      const iconElement = iconEl.value
      const labelElement = labelEl.value

      if (iconElement && labelElement) {
        viewTransition.then(() => {
          Object.assign(iconElement.style, {
            transform: 'translateY(20px)',
            opacity: '0',
          })
          Object.assign(labelElement.style, {
            transform: 'translateY(20px)',
            opacity: '0',
          })

          nextTick(() => {
            enterElement(
              iconElement,
              { moveDuration: 300, opacityDuration: 300 },
              activeAnimations,
            )
            enterElement(
              labelElement,
              { moveDuration: 300, opacityDuration: 150 },
              activeAnimations,
            )
          })
        })
      }

      return true
    }

    const iconElement = iconEl.value
    const labelElement = labelEl.value

    if (!iconElement || !labelElement) {
      updateIcon(newTag.icon)
      updateText(newTag.text)

      return false
    }

    // Cancel any in-flight animations from a previous grade change
    cancelActiveAnimations()
    cleanupElement(iconElement)
    cleanupElement(labelElement)

    const chipElement = chipEl.value
    if (chipElement) {
      chipElement.style.width = ''
    }

    const stagger = index * PILL_STAGGER_MS

    // Freeze the chip at its current width so the pill doesn't snap
    // when content changes at opacity 0 during the exit phase.
    const oldWidth = chipElement?.offsetWidth

    if (chipElement && oldWidth) {
      chipElement.style.width = `${oldWidth}px`
    }

    // Signal that a swap animation is starting so the premium chip
    // entrance waits until all swaps (exit + FLIP) have settled.
    trackSwapStart()

    // --- Phase 1: Exit (icon & label fade out in parallel) ---
    const iconExit = exitElement(
      iconElement,
      stagger,
      { moveDelay: 0, opacityDelay: 150, opacityDuration: 300 },
      activeAnimations,
    )

    const labelExit = exitElement(
      labelElement,
      stagger,
      { moveDelay: 150, opacityDelay: 300, opacityDuration: 150 },
      activeAnimations,
    )

    // --- Phase 2: Swap content + FLIP siblings, then Phase 3: Enter ---
    Promise.all([iconExit, labelExit])
      .then(() => {
        // Swap content while icon & label are at opacity 0
        updateIcon(newTag.icon)
        updateText(newTag.text)

        nextTick(() => {
          // FLIP siblings if the chip width changed
          const didFlip =
            chipElement && oldWidth
              ? flipSiblingsOnWidthChange(chipElement)
              : false

          const startEnter = () => {
            // Signal that this chip's swap has settled (FLIP done).
            // Premium chip entrance awaits this before its 600 ms delay.
            trackSwapSettled()

            enterElement(
              iconElement,
              { moveDuration: 300, opacityDuration: 300 },
              activeAnimations,
            )
            enterElement(
              labelElement,
              { moveDuration: 300, opacityDuration: 150 },
              activeAnimations,
            )
          }

          if (didFlip) {
            // Wait for siblings to settle, then enter
            setTimeout(() => nextTick(startEnter), FLIP_DURATION_MS)
          } else {
            nextTick(startEnter)
          }
        })
      })
      .catch(() => {
        // Animation was cancelled (e.g. rapid grade change) — apply final state
        trackSwapSettled()
        updateIcon(newTag.icon)
        updateText(newTag.text)
        cleanupElement(iconElement)
        cleanupElement(labelElement)
      })

    return true
  }

  return { animateSwap }
}
